import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, CheckCircle2, AlertTriangle, ListTodo, KanbanSquare, TrendingUp, Map, Activity, BarChart3, Shield, ArrowRight } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageSkeleton } from "../components/Skeleton";

function StatCard({ icon: Icon, label, value, color, testid, onClick, delay = 0 }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{ animationDelay: `${delay}ms` }}
      className="text-left w-full bg-[#121214] border border-white/10 rounded-xl p-5 hover:border-white/25 hover:-translate-y-0.5 transition-[border-color,transform] cursor-pointer animate-fade-up group"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">{label}</span>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="flex items-end justify-between mt-3">
        <p className="font-display text-3xl font-extrabold">{value}</p>
        <ArrowRight className="h-4 w-4 text-zinc-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-[opacity,transform]" />
      </div>
    </button>
  );
}

function QuickAccessCard({ icon: Icon, label, sub, color, testid, onClick, delay = 0 }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{ animationDelay: `${delay}ms` }}
      className="text-left w-full bg-[#121214] border border-white/10 rounded-xl p-4 hover:border-white/25 hover:-translate-y-0.5 transition-[border-color,transform] cursor-pointer group flex items-center gap-3 animate-fade-up"
    >
      <div className="h-10 w-10 shrink-0 rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm truncate">{label}</p>
        <p className="text-xs text-zinc-500 truncate">{sub}</p>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-zinc-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-[opacity,transform]" />
    </button>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [details, setDetails] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/task-details").then((r) => setDetails(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return <PageSkeleton />;
  }

  return (
    <div className="h-screen overflow-y-auto p-6 lg:p-10">
      <div className="flex items-center gap-3 mb-1">
        <LayoutDashboard className="h-6 w-6 text-primary" />
        <h1 className="font-display text-3xl font-extrabold">Panel</h1>
      </div>
      <p className="text-zinc-500 mb-8">Hola {user?.name?.split(" ")[0]}, aquí está el resumen de tu trabajo.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-total" icon={ListTodo} label="Tareas totales" value={stats.total_tasks} color="#00B0FF" onClick={() => navigate("/boards")} delay={0} />
        <StatCard testid="stat-completed" icon={CheckCircle2} label="Completadas" value={stats.completed_tasks} color="#00E676" onClick={() => navigate("/reports")} delay={60} />
        <StatCard testid="stat-overdue" icon={AlertTriangle} label="Atrasadas" value={stats.overdue_tasks} color="#FF1744" onClick={() => navigate("/my-tasks")} delay={120} />
        <StatCard testid="stat-mine" icon={TrendingUp} label="Mis tareas" value={stats.my_tasks} color="#FFD600" onClick={() => navigate("/my-tasks")} delay={180} />
      </div>

      <div className="mb-8 lg:hidden">
        <h3 className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-3">Accesos rápidos</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <QuickAccessCard testid="quick-mytasks" icon={ListTodo} label="Mis Tareas" color="#FFD600"
            sub={`${stats.my_pending_tasks} pendiente${stats.my_pending_tasks !== 1 ? "s" : ""}`}
            onClick={() => navigate("/my-tasks")} />
          <QuickAccessCard testid="quick-boards" icon={KanbanSquare} label="Tableros" color="#00B0FF"
            sub={`${stats.total_boards} activo${stats.total_boards !== 1 ? "s" : ""}`}
            onClick={() => navigate("/boards")} />
          <QuickAccessCard testid="quick-roadmap" icon={Map} label="Roadmap" color="#D500F9"
            sub={`${stats.roadmap_tasks} tarea${stats.roadmap_tasks !== 1 ? "s" : ""} planificada${stats.roadmap_tasks !== 1 ? "s" : ""}`}
            onClick={() => navigate("/roadmap")} />
          <QuickAccessCard testid="quick-activity" icon={Activity} label="Actividad" color="#00E676"
            sub={`${stats.recent_activity} evento${stats.recent_activity !== 1 ? "s" : ""} esta semana`}
            onClick={() => navigate("/activity")} />
          <QuickAccessCard testid="quick-reports" icon={BarChart3} label="Reportes" color="#FF6D00"
            sub={`${stats.completion_rate}% completado`}
            onClick={() => navigate("/reports")} />
          {user?.role === "admin" && (
            <QuickAccessCard testid="quick-admin" icon={Shield} label="Administración" color="#FF1744"
              sub={`${stats.total_users ?? "—"} usuario${stats.total_users !== 1 ? "s" : ""}`}
              onClick={() => navigate("/admin")} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-[#121214] border border-white/10 rounded-xl p-6 lg:col-span-1">
          <h3 className="font-display font-bold text-lg mb-1">Progreso</h3>
          <p className="text-zinc-500 text-sm mb-4">Tasa de finalización</p>
          <div className="relative h-44 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={[{ value: stats.completion_rate }, { value: 100 - stats.completion_rate }]} dataKey="value" innerRadius={58} outerRadius={75} startAngle={90} endAngle={-270} stroke="none">
                  <Cell fill="#00E676" />
                  <Cell fill="#1C1C1F" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-3xl font-extrabold">{stats.completion_rate}%</span>
              <span className="text-xs text-zinc-500">completado</span>
            </div>
          </div>
        </div>

        <div className="bg-[#121214] border border-white/10 rounded-xl p-6 lg:col-span-2">
          <h3 className="font-display font-bold text-lg mb-1">Tareas por estado</h3>
          <p className="text-zinc-500 text-sm mb-4">Distribución actual</p>
          {stats.status_chart.length === 0 ? (
            <p className="text-zinc-600 text-sm py-12 text-center">Aún no hay tareas.</p>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats.status_chart}>
                <XAxis dataKey="name" tick={{ fill: "#71717A", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#71717A", fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#1C1C1F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {stats.status_chart.map((s, i) => <Cell key={`${s.name}-${i}`} fill={s.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-lg">Tableros</h3>
            <p className="text-zinc-500 text-sm">Tienes {stats.total_boards} tableros</p>
          </div>
          <button onClick={() => navigate("/boards")} data-testid="goto-boards" className="text-sm font-semibold text-primary hover:underline flex items-center gap-1">
            <KanbanSquare className="h-4 w-4" /> Ver todos
          </button>
        </div>
        {stats.boards_overview.length === 0 ? (
          <p className="text-zinc-600 text-sm py-6 text-center">Crea tu primer tablero para empezar.</p>
        ) : (
          <div className="space-y-2">
            {stats.boards_overview.map((b, i) => (
              <div key={`${b.name}-${i}`} className="flex items-center justify-between bg-[#1C1C1F] rounded-lg px-4 py-3">
                <span className="font-medium">{b.name}</span>
                <span className="text-sm text-zinc-500">{b.tasks} tareas</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl p-6 mt-8" data-testid="dashboard-task-details">
        <div className="mb-4">
          <h3 className="font-display font-bold text-lg">Detalle de tareas</h3>
          <p className="text-zinc-500 text-sm">Quién creó cada tarea, su avance y la última actualización</p>
        </div>
        {details.length === 0 ? (
          <p className="text-zinc-600 text-sm py-6 text-center">Aún no hay tareas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                  <th className="py-2 pr-4 font-semibold">Tarea</th>
                  <th className="py-2 pr-4 font-semibold">Creada por</th>
                  <th className="py-2 pr-4 font-semibold">Avance</th>
                  <th className="py-2 font-semibold">Última actualización</th>
                </tr>
              </thead>
              <tbody>
                {details.map((t, i) => (
                  <tr key={t.id} data-testid={`task-detail-row-${t.id}`} className="border-b border-white/5 animate-fade-up" style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}>
                    <td className="py-2.5 pr-4">
                      <p className="font-medium truncate max-w-[240px]">{t.title}</p>
                      <p className="text-[11px] text-zinc-600 flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: t.status_color }} />
                        {t.board_name} · {t.status_title}
                      </p>
                    </td>
                    <td className="py-2.5 pr-4">
                      {t.creator ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ background: t.creator.color }}>{t.creator.name[0]}</span>
                          <span className="text-zinc-300">{t.creator.name}</span>
                        </span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${t.progress}%`, background: t.is_done ? "#00E676" : "#00B0FF" }} />
                        </div>
                        <span className={`text-xs font-bold ${t.is_done ? "text-[#00E676]" : ""}`}>{t.progress}%</span>
                      </div>
                    </td>
                    <td className="py-2.5">
                      {t.last_update ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ background: t.last_update.color }}>{t.last_update.name[0]}</span>
                          <span className="text-zinc-300">{t.last_update.name}</span>
                          <span className="text-[11px] text-zinc-600">· {new Date(t.last_update.at).toLocaleDateString("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                        </span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
